import React, { useState, useEffect } from 'react';
import { useQuery, useLazyQuery, gql } from '@apollo/client';
import { InvoiceQueryResult, ReductionResult } from '../graphql/manualAdjustmentOperations';

// SVG Icons
const SearchIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const RefreshIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" /><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </svg>
);

const TrendDownIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 18 13.5 8.5 8.5 13.5 1 6" /><polyline points="17 18 23 18 23 12" />
  </svg>
);

const TrendUpIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" /><polyline points="17 6 23 6 23 12" />
  </svg>
);

const TrashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const AlertIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const InfoIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

const GET_INVOICE_PREFIXES = gql`
  query GetInvoicePrefixes {
    getInvoicePrefixes { id prefix resolutionNumber isActive }
  }
`;

const QUERY_INVOICES_BY_PREFIX_AND_DATE_RANGE = gql`
  query QueryInvoicesByPrefixAndDateRange($prefix: String!, $startDate: DateTime!, $endDate: DateTime!, $resolutionId: Int) {
    queryInvoicesByPrefixAndDateRange(prefix: $prefix, startDate: $startDate, endDate: $endDate, resolutionId: $resolutionId) {
      totalInvoices totalAmount firstInvoiceNumber lastInvoiceNumber prefix startDate endDate
      invoices { id invoiceNumber total subtotal tax tip createdAt status hasElectronic customerName }
    }
  }
`;

export const ManualInvoiceAdjustmentPanel: React.FC = () => {
  const [processing, setProcessing] = useState(false);
  const [selectedResolutionId, setSelectedResolutionId] = useState<number | null>(null);
  const { data: prefixesData, loading: prefixesLoading } = useQuery(GET_INVOICE_PREFIXES);
  const [queryInvoicesByPrefixAndDateRange, { loading: querying, data: queryData, error: queryError }] = useLazyQuery(QUERY_INVOICES_BY_PREFIX_AND_DATE_RANGE);

  const [startDate, setStartDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [startTime, setStartTime] = useState<string>('00:00');
  const [endDate, setEndDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [endTime, setEndTime] = useState<string>('23:59');
  const [queryResult, setQueryResult] = useState<InvoiceQueryResult | null>(null);
  const [adjustmentType, setAdjustmentType] = useState<'reduce' | 'increase' | 'lines'>('reduce');
  const [adjustmentAmount, setAdjustmentAmount] = useState<string>('');
  const [exemptPercentage, setExemptPercentage] = useState<string>('50');
  const [useExemptPercentage, setUseExemptPercentage] = useState(false);
  const [adjustmentResult, setAdjustmentResult] = useState<ReductionResult | null>(null);
  const [linesOptions, setLinesOptions] = useState<Array<{ linesToRemove: number; newTotal: number; averageLinesPerInvoice: number; }>>([]);
  const [selectedLinesOption, setSelectedLinesOption] = useState<number | null>(null);

  const INVOICES_PER_PAGE = 30;
  const [invoicesPage, setInvoicesPage] = useState(1);
  const resolutions = prefixesData?.getInvoicePrefixes || [];

  const formatCurrency = (amount: number): string => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(amount);

  const processAdjustment = async (): Promise<ReductionResult | undefined> => {
    setProcessing(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      const amount = parseFloat(adjustmentAmount);
      const isIncrease = adjustmentType === 'increase';
      const newTotal = isIncrease ? (queryResult?.totalAmount || 0) + amount : (queryResult?.totalAmount || 0) - amount;
      return {
        success: true, originalTotal: queryResult?.totalAmount || 0, reductionAmount: amount, newTotal,
        invoicesAdjusted: queryResult?.totalInvoices || 0, invoicesDeleted: 0,
        newFirstInvoice: queryResult?.firstInvoiceNumber || '', newLastInvoice: queryResult?.lastInvoiceNumber || '',
        message: `${isIncrease ? 'Aumento' : 'Reducción'} procesado (modo demo)`,
      };
    } catch { return undefined; }
    finally { setProcessing(false); }
  };

  const calculateLinesOptions = () => {
    if (!queryResult) return;
    const totalInvoices = queryResult.totalInvoices;
    const currentTotal = queryResult.totalAmount;
    const avgLinesPerInvoice = 4;
    const avgValuePerLine = currentTotal / (totalInvoices * avgLinesPerInvoice);
    const options = [];
    for (let i = 1; i <= 8; i++) {
      const totalLinesToRemove = totalInvoices * i;
      const estimatedReduction = totalLinesToRemove * avgValuePerLine;
      options.push({ linesToRemove: i, newTotal: Math.max(0, currentTotal - estimatedReduction), averageLinesPerInvoice: avgLinesPerInvoice });
    }
    setLinesOptions(options);
  };

  useEffect(() => {
    if (resolutions.length > 0 && selectedResolutionId === null) setSelectedResolutionId(resolutions[0].id);
  }, [resolutions, selectedResolutionId]);

  useEffect(() => {
    if (queryData?.queryInvoicesByPrefixAndDateRange) {
      const data = queryData.queryInvoicesByPrefixAndDateRange;
      setQueryResult({ totalInvoices: data.totalInvoices, totalAmount: data.totalAmount, firstInvoiceNumber: data.firstInvoiceNumber || '', lastInvoiceNumber: data.lastInvoiceNumber || '', invoices: data.invoices || [] });
    }
  }, [queryData]);

  useEffect(() => { if (queryResult && adjustmentType === 'lines') calculateLinesOptions(); }, [queryResult, adjustmentType]);
  useEffect(() => { if (queryError) alert('Error al consultar facturas.'); }, [queryError]);

  const handleQueryInvoices = async () => {
    setAdjustmentResult(null); setQueryResult(null); setInvoicesPage(1);
    const selectedResolution = resolutions.find((r: any) => r.id === selectedResolutionId);
    if (!selectedResolution) { alert('Seleccione una resolución.'); return; }
    try {
      await queryInvoicesByPrefixAndDateRange({
        variables: { prefix: selectedResolution.prefix, startDate: new Date(`${startDate}T${startTime}:00`).toISOString(), endDate: new Date(`${endDate}T${endTime}:00`).toISOString(), resolutionId: selectedResolutionId }
      });
    } catch { alert('Error al consultar.'); }
  };

  const calculateAdjustment = (): ReductionResult | null => {
    if (!queryResult || !adjustmentAmount) return null;
    const amount = parseFloat(adjustmentAmount);
    if (isNaN(amount) || amount <= 0) return null;
    const originalTotal = queryResult.totalAmount;
    const isIncrease = adjustmentType === 'increase';
    const newTotal = isIncrease ? originalTotal + amount : originalTotal - amount;
    if (!isIncrease && newTotal < 0) { alert('Monto mayor que el total.'); return null; }
    return { success: true, message: '', originalTotal, reductionAmount: amount, newTotal, invoicesAdjusted: queryResult.totalInvoices, invoicesDeleted: 0, newFirstInvoice: queryResult.firstInvoiceNumber, newLastInvoice: queryResult.lastInvoiceNumber };
  };

  const handleProcessAdjustment = async () => {
    const calculation = calculateAdjustment();
    if (!calculation) return;
    const isIncrease = adjustmentType === 'increase';
    if (!confirm(`¿Procesar ${isIncrease ? 'aumento' : 'reducción'}?\n\n${formatCurrency(calculation.originalTotal)} → ${formatCurrency(calculation.newTotal)}`)) return;
    try {
      const result = await processAdjustment();
      if (result?.success) { setAdjustmentResult(result); setQueryResult(null); setAdjustmentAmount(''); alert('Procesado exitosamente.'); }
    } catch { alert('Error al procesar.'); }
  };

  const handleProcessLinesRemoval = async () => {
    if (selectedLinesOption === null || !queryResult) return;
    const selectedOption = linesOptions.find(opt => opt.linesToRemove === selectedLinesOption);
    if (!selectedOption) return;
    if (!confirm(`¿Eliminar ${selectedLinesOption} línea(s) por factura?`)) return;
    setProcessing(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      setAdjustmentResult({
        success: true, originalTotal: queryResult.totalAmount, reductionAmount: queryResult.totalAmount - selectedOption.newTotal,
        newTotal: selectedOption.newTotal, invoicesAdjusted: queryResult.totalInvoices, invoicesDeleted: 0,
        newFirstInvoice: queryResult.firstInvoiceNumber, newLastInvoice: queryResult.lastInvoiceNumber,
        message: `${selectedLinesOption} línea(s) eliminada(s) (demo)`
      });
      setQueryResult(null); setLinesOptions([]); setSelectedLinesOption(null);
      alert('Procesado.');
    } catch { alert('Error.'); }
    finally { setProcessing(false); }
  };

  const handleReset = () => { setQueryResult(null); setAdjustmentResult(null); setAdjustmentAmount(''); setExemptPercentage('50'); setUseExemptPercentage(false); setLinesOptions([]); setSelectedLinesOption(null); setInvoicesPage(1); };

  const isIncrease = adjustmentType === 'increase';

  return (
    <div className="miap-panel">
      <div className="miap-header">
        <h2>Ajustar Facturas Manualmente</h2>
        <p>Aumenta, reduce montos o elimina líneas de productos</p>
      </div>

      {/* Query Section */}
      <div className="miap-card">
        <h3>1. Consultar Facturas</h3>
        <div className="miap-form">
          <div className="miap-form-row">
            <div className="miap-form-group miap-full">
              <label>Resolución</label>
              <select value={selectedResolutionId ?? ''} onChange={(e) => setSelectedResolutionId(e.target.value ? Number(e.target.value) : null)} disabled={querying || prefixesLoading}>
                {prefixesLoading ? <option value="">Cargando...</option> : resolutions.length === 0 ? <option value="">Sin resoluciones</option> :
                  resolutions.map((r: any) => <option key={r.id} value={r.id}>{r.prefix}{r.resolutionNumber ? ` (${r.resolutionNumber})` : ''}{!r.isActive ? ' - INACTIVA' : ''}</option>)}
              </select>
            </div>
          </div>
          <div className="miap-form-row miap-date-row">
            <div className="miap-form-group"><label>Fecha Inicio</label><input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} disabled={querying} /></div>
            <div className="miap-form-group"><label>Hora</label><input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} disabled={querying} /></div>
            <div className="miap-form-group"><label>Fecha Fin</label><input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} disabled={querying} /></div>
            <div className="miap-form-group"><label>Hora</label><input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} disabled={querying} /></div>
          </div>
          <button className="miap-btn miap-btn-primary miap-btn-lg" onClick={handleQueryInvoices} disabled={querying || selectedResolutionId === null}>
            {querying ? <><RefreshIcon /> Consultando...</> : <><SearchIcon /> Consultar</>}
          </button>
        </div>

        {queryResult && (
          <div className="miap-results">
            <h4>Resultados</h4>
            <div className="miap-stats-grid">
              <div className="miap-stat"><span>Facturas</span><strong>{queryResult.totalInvoices}</strong></div>
              <div className="miap-stat"><span>Total</span><strong>{formatCurrency(queryResult.totalAmount)}</strong></div>
              <div className="miap-stat"><span>Primera</span><strong>{queryResult.firstInvoiceNumber || '-'}</strong></div>
              <div className="miap-stat"><span>Última</span><strong>{queryResult.lastInvoiceNumber || '-'}</strong></div>
            </div>
            {queryResult.totalInvoices === 0 && (
              <div className="miap-no-results"><AlertIcon /> No se encontraron facturas</div>
            )}

            {queryResult.invoices && queryResult.invoices.length > 0 && (() => {
              const totalPages = Math.ceil(queryResult.invoices.length / INVOICES_PER_PAGE);
              const startIndex = (invoicesPage - 1) * INVOICES_PER_PAGE;
              const paginatedInvoices = queryResult.invoices.slice(startIndex, startIndex + INVOICES_PER_PAGE);
              return (
                <div className="miap-table-section">
                  <h4>Facturas ({queryResult.invoices.length})</h4>
                  <div className="miap-table-wrapper">
                    <table className="miap-table">
                      <thead><tr><th>N°</th><th>Cliente</th><th>Fecha</th><th>Total</th><th>Estado</th></tr></thead>
                      <tbody>
                        {paginatedInvoices.map((inv: any) => (
                          <tr key={inv.id}>
                            <td>{inv.invoiceNumber}</td>
                            <td>{inv.customerName}</td>
                            <td>{new Date(inv.createdAt).toLocaleDateString('es-CO')}</td>
                            <td className="miap-amount">{formatCurrency(inv.total)}</td>
                            <td><span className={`miap-badge ${inv.status?.toLowerCase()}`}>{inv.status === 'pagada' ? 'Pagada' : 'Por Pagar'}</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {totalPages > 1 && (
                    <div className="miap-pagination">
                      <button onClick={() => setInvoicesPage(p => Math.max(1, p - 1))} disabled={invoicesPage === 1}>Anterior</button>
                      <span>Pág. {invoicesPage} de {totalPages}</span>
                      <button onClick={() => setInvoicesPage(p => Math.min(totalPages, p + 1))} disabled={invoicesPage === totalPages}>Siguiente</button>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        )}
      </div>

      {/* Adjustment Section */}
      {queryResult && queryResult.totalInvoices > 0 && (
        <div className="miap-card">
          <h3>2. Seleccionar Operación</h3>
          <div className="miap-type-selector">
            <button className={`miap-type-btn ${adjustmentType === 'reduce' ? 'active' : ''}`} onClick={() => setAdjustmentType('reduce')} disabled={processing}>
              <TrendDownIcon /> Reducir
            </button>
            <button className={`miap-type-btn ${adjustmentType === 'increase' ? 'active' : ''}`} onClick={() => setAdjustmentType('increase')} disabled={processing}>
              <TrendUpIcon /> Aumentar
            </button>
            <button className={`miap-type-btn ${adjustmentType === 'lines' ? 'active' : ''}`} onClick={() => setAdjustmentType('lines')} disabled={processing}>
              <TrashIcon /> Líneas
            </button>
          </div>

          {(adjustmentType === 'reduce' || adjustmentType === 'increase') && (
            <div className="miap-adj-form">
              <div className="miap-form-group">
                <label>Monto a {isIncrease ? 'Aumentar' : 'Reducir'}</label>
                <div className="miap-input-prefix">
                  <span>$</span>
                  <input type="number" value={adjustmentAmount} onChange={(e) => setAdjustmentAmount(e.target.value)} disabled={processing} min="0" step="1000" placeholder="0" />
                </div>
              </div>

              <label className="miap-checkbox">
                <input type="checkbox" checked={useExemptPercentage} onChange={(e) => setUseExemptPercentage(e.target.checked)} disabled={processing} />
                Especificar % para excluidos
              </label>

              {useExemptPercentage && (
                <div className="miap-form-group">
                  <label>Porcentaje excluidos</label>
                  <div className="miap-input-suffix">
                    <input type="number" value={exemptPercentage} onChange={(e) => setExemptPercentage(e.target.value)} disabled={processing} min="0" max="100" />
                    <span>%</span>
                  </div>
                </div>
              )}

              {adjustmentAmount && parseFloat(adjustmentAmount) > 0 && (
                <div className="miap-preview">
                  <h4>Vista Previa</h4>
                  <div className="miap-preview-grid">
                    <div><span>Original</span><strong>{formatCurrency(queryResult.totalAmount)}</strong></div>
                    <div className={isIncrease ? 'up' : 'down'}><span>{isIncrease ? 'Aumento' : 'Reducción'}</span><strong>{isIncrease ? '+' : '-'}{formatCurrency(parseFloat(adjustmentAmount))}</strong></div>
                    <div className="total"><span>Nuevo</span><strong>{formatCurrency(isIncrease ? queryResult.totalAmount + parseFloat(adjustmentAmount) : queryResult.totalAmount - parseFloat(adjustmentAmount))}</strong></div>
                  </div>
                </div>
              )}

              <div className="miap-actions">
                <button className="miap-btn miap-btn-secondary" onClick={handleReset} disabled={processing}>Reiniciar</button>
                <button className="miap-btn miap-btn-primary" onClick={handleProcessAdjustment} disabled={processing || !adjustmentAmount || parseFloat(adjustmentAmount) <= 0}>
                  {processing ? 'Procesando...' : `Procesar ${isIncrease ? 'Aumento' : 'Reducción'}`}
                </button>
              </div>
            </div>
          )}

          {adjustmentType === 'lines' && (
            <div className="miap-lines-form">
              <p className="miap-lines-intro">Seleccione líneas a eliminar por factura:</p>
              <div className="miap-lines-grid">
                {linesOptions.map((opt) => (
                  <div key={opt.linesToRemove} className={`miap-line-card ${selectedLinesOption === opt.linesToRemove ? 'selected' : ''}`} onClick={() => setSelectedLinesOption(opt.linesToRemove)}>
                    <span className="miap-line-num">{opt.linesToRemove}</span>
                    <span className="miap-line-label">línea(s)</span>
                    <span className="miap-line-total">{formatCurrency(opt.newTotal)}</span>
                    {selectedLinesOption === opt.linesToRemove && <CheckIcon />}
                  </div>
                ))}
              </div>
              <div className="miap-actions">
                <button className="miap-btn miap-btn-secondary" onClick={handleReset} disabled={processing}>Reiniciar</button>
                <button className="miap-btn miap-btn-primary" onClick={handleProcessLinesRemoval} disabled={processing || selectedLinesOption === null}>
                  {processing ? 'Procesando...' : 'Procesar'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Results */}
      {adjustmentResult && (
        <div className="miap-card miap-success-card">
          <h3><CheckIcon /> Completado</h3>
          <p>{adjustmentResult.message}</p>
          <div className="miap-summary-grid">
            <div><span>Original</span><strong>{formatCurrency(adjustmentResult.originalTotal)}</strong></div>
            <div className={isIncrease ? 'up' : 'down'}><span>{isIncrease ? 'Aumento' : 'Reducción'}</span><strong>{isIncrease ? '+' : '-'}{formatCurrency(adjustmentResult.reductionAmount)}</strong></div>
            <div className="total"><span>Nuevo</span><strong>{formatCurrency(adjustmentResult.newTotal)}</strong></div>
          </div>
          <button className="miap-btn miap-btn-primary" onClick={handleReset}>Nueva Consulta</button>
        </div>
      )}

      {/* Info */}
      <div className="miap-card miap-info-card">
        <h4><InfoIcon /> Información</h4>
        <ul>
          <li><strong>Reducir/Aumentar:</strong> Distribución proporcional entre facturas</li>
          <li><strong>Líneas:</strong> Elimina productos completos de cada factura</li>
          <li><strong>IVA:</strong> Se recalcula automáticamente</li>
          <li><strong>Irreversible:</strong> Verifique antes de procesar</li>
        </ul>
      </div>

      <style>{styles}</style>
    </div>
  );
};

const styles = `
  .miap-panel { max-width: 1000px; margin: 0 auto; }

  .miap-header {
    padding: 16px 20px;
    background: linear-gradient(135deg, #1e40af 0%, #2563eb 100%);
    border-radius: 8px;
    color: #fff;
    margin-bottom: 16px;
  }
  .miap-header h2 { margin: 0 0 4px 0; font-size: 1rem; }
  .miap-header p { margin: 0; font-size: 0.75rem; opacity: 0.9; }

  .miap-card {
    background: #fff;
    border: 1px solid #dbeafe;
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 12px;
  }
  .miap-card h3 { margin: 0 0 12px 0; font-size: 0.85rem; color: #1e3a8a; display: flex; align-items: center; gap: 6px; }
  .miap-card h4 { margin: 0 0 10px 0; font-size: 0.8rem; color: #1e3a8a; display: flex; align-items: center; gap: 6px; }

  .miap-form { display: flex; flex-direction: column; gap: 12px; }
  .miap-form-row { display: flex; gap: 10px; flex-wrap: wrap; }
  .miap-date-row { display: grid; grid-template-columns: 1fr 80px 1fr 80px; gap: 8px; }
  .miap-form-group { display: flex; flex-direction: column; gap: 4px; flex: 1; }
  .miap-form-group.miap-full { flex: 1 0 100%; }
  .miap-form-group label { font-size: 0.7rem; color: #64748b; }
  .miap-form-group input, .miap-form-group select {
    padding: 8px 10px;
    border: 1px solid #dbeafe;
    border-radius: 4px;
    font-size: 0.8rem;
  }

  .miap-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 14px;
    border: none;
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s;
  }
  .miap-btn:disabled { opacity: 0.5; cursor: not-allowed; }
  .miap-btn-primary { background: #2563eb; color: #fff; }
  .miap-btn-primary:hover:not(:disabled) { background: #1e40af; }
  .miap-btn-secondary { background: #e2e8f0; color: #475569; }
  .miap-btn-secondary:hover:not(:disabled) { background: #cbd5e1; }
  .miap-btn-lg { padding: 10px 20px; font-size: 0.8rem; }

  .miap-results { margin-top: 16px; padding-top: 16px; border-top: 1px solid #e2e8f0; }

  .miap-stats-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 10px;
    margin-bottom: 12px;
  }
  .miap-stat {
    text-align: center;
    padding: 10px;
    background: #f8fafc;
    border-radius: 6px;
    border: 1px solid #e2e8f0;
  }
  .miap-stat span { display: block; font-size: 0.65rem; color: #64748b; margin-bottom: 2px; }
  .miap-stat strong { font-size: 0.9rem; color: #1e3a8a; }

  .miap-no-results {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px;
    background: #fef3c7;
    border-radius: 6px;
    color: #92400e;
    font-size: 0.75rem;
  }

  .miap-table-section { margin-top: 16px; }
  .miap-table-wrapper { overflow-x: auto; }
  .miap-table { width: 100%; border-collapse: collapse; font-size: 0.75rem; }
  .miap-table th, .miap-table td { padding: 8px 10px; text-align: left; border-bottom: 1px solid #e2e8f0; }
  .miap-table th { background: #f8fafc; font-weight: 600; color: #64748b; font-size: 0.65rem; text-transform: uppercase; }
  .miap-table tr:hover { background: #f8fafc; }
  .miap-amount { font-weight: 600; color: #1e3a8a; text-align: right; }
  .miap-badge { padding: 2px 6px; border-radius: 4px; font-size: 0.65rem; }
  .miap-badge.pagada { background: #dcfce7; color: #166534; }
  .miap-badge.por_pagar { background: #fef3c7; color: #92400e; }

  .miap-pagination {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 12px;
    margin-top: 12px;
    font-size: 0.75rem;
  }
  .miap-pagination button {
    padding: 6px 12px;
    border: 1px solid #dbeafe;
    border-radius: 4px;
    background: #fff;
    cursor: pointer;
    font-size: 0.7rem;
  }
  .miap-pagination button:disabled { opacity: 0.5; }

  .miap-type-selector {
    display: flex;
    gap: 8px;
    margin-bottom: 16px;
  }
  .miap-type-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 10px 16px;
    border: 2px solid #dbeafe;
    border-radius: 6px;
    background: #fff;
    color: #64748b;
    font-size: 0.75rem;
    cursor: pointer;
    transition: all 0.15s;
  }
  .miap-type-btn:hover { border-color: #3b82f6; color: #1e40af; }
  .miap-type-btn.active { background: #2563eb; border-color: #2563eb; color: #fff; }

  .miap-adj-form { display: flex; flex-direction: column; gap: 12px; }

  .miap-input-prefix, .miap-input-suffix {
    display: flex;
    align-items: center;
  }
  .miap-input-prefix span, .miap-input-suffix span {
    padding: 8px 10px;
    background: #f8fafc;
    border: 1px solid #dbeafe;
    color: #64748b;
    font-size: 0.8rem;
  }
  .miap-input-prefix span { border-right: none; border-radius: 4px 0 0 4px; }
  .miap-input-suffix span { border-left: none; border-radius: 0 4px 4px 0; }
  .miap-input-prefix input, .miap-input-suffix input {
    flex: 1;
    padding: 8px 10px;
    border: 1px solid #dbeafe;
    font-size: 0.85rem;
  }
  .miap-input-prefix input { border-radius: 0 4px 4px 0; }
  .miap-input-suffix input { border-radius: 4px 0 0 4px; }

  .miap-checkbox {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 0.75rem;
    color: #475569;
    cursor: pointer;
  }
  .miap-checkbox input { width: 16px; height: 16px; }

  .miap-preview {
    padding: 12px;
    background: #eff6ff;
    border-radius: 6px;
  }
  .miap-preview h4 { margin-bottom: 10px; }
  .miap-preview-grid, .miap-summary-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;
  }
  .miap-preview-grid > div, .miap-summary-grid > div {
    text-align: center;
    padding: 8px;
    background: #fff;
    border-radius: 4px;
  }
  .miap-preview-grid span, .miap-summary-grid span { display: block; font-size: 0.65rem; color: #64748b; }
  .miap-preview-grid strong, .miap-summary-grid strong { font-size: 0.85rem; color: #1e3a8a; }
  .miap-preview-grid .down strong { color: #b91c1c; }
  .miap-preview-grid .up strong { color: #166534; }
  .miap-preview-grid .total, .miap-summary-grid .total { background: #dbeafe; }

  .miap-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 12px; }

  .miap-lines-form { display: flex; flex-direction: column; gap: 12px; }
  .miap-lines-intro { font-size: 0.75rem; color: #64748b; margin: 0; }
  .miap-lines-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; }
  .miap-line-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 12px;
    border: 2px solid #dbeafe;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.15s;
    position: relative;
  }
  .miap-line-card:hover { border-color: #3b82f6; }
  .miap-line-card.selected { border-color: #2563eb; background: #eff6ff; }
  .miap-line-card svg { position: absolute; top: 6px; right: 6px; color: #2563eb; }
  .miap-line-num { font-size: 1.2rem; font-weight: 700; color: #1e3a8a; }
  .miap-line-label { font-size: 0.65rem; color: #64748b; }
  .miap-line-total { font-size: 0.75rem; color: #1e40af; font-weight: 600; margin-top: 4px; }

  .miap-success-card { border-left: 4px solid #22c55e; }
  .miap-success-card h3 { color: #166534; }
  .miap-success-card > p { font-size: 0.75rem; color: #166534; margin: 0 0 12px 0; }

  .miap-info-card { background: #eff6ff; border-color: #93c5fd; }
  .miap-info-card h4 { color: #1e40af; }
  .miap-info-card ul { margin: 0; padding-left: 16px; font-size: 0.7rem; color: #1e3a8a; }
  .miap-info-card li { margin-bottom: 4px; }

  @media (max-width: 700px) {
    .miap-stats-grid { grid-template-columns: repeat(2, 1fr); }
    .miap-date-row { grid-template-columns: 1fr 1fr; }
    .miap-lines-grid { grid-template-columns: repeat(2, 1fr); }
    .miap-preview-grid, .miap-summary-grid { grid-template-columns: 1fr; }
  }
`;
